// AccessDetailDrawer — the My Access detail card for a granted access. Opens
// when an access row is clicked. Wraps the shared DetailCard with a
// tone-gradient status card (time remaining / reviewer / grant window) and a
// footer that Extends or Revokes an active grant (the same paths as the row's
// inline buttons) or re-opens the request flow to Renew an expired one.

import { Box, Flex, HStack, Icon, Text, useToast } from "@chakra-ui/react";
import { isAfter } from "date-fns";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TbCircleCheck, TbClock, TbClockX, TbRefresh } from "react-icons/tb";

import Avatar from "components/ds/Avatar";
import Bar from "components/ds/Bar";
import PloyButton from "components/ds/PloyButton";
import SignedBadge from "components/passkeys/SignedBadge";
import { RequestAccessModal } from "components/RequestAccess";
import { humanDt } from "modules/utils";
import {
  MF_ACCESS,
  type MFRequestLine,
  type MFSelectableResourceAccess,
} from "state/members/access_v2/catalogs";

import RequestLines from "pages/Members/Access/Catalogs/RequestLines";

import { accessRowIdentity } from "./AccessGroup";
import { useRevokeAccessFlow } from "./useRevokeAccessFlow";
import DetailCard, { MetaRow } from "./DetailCard";
import {
  accessMetrics,
  expiredAgoLabel,
  grantWindow,
  remainingBar,
  remainingLabel,
  resolveGrantActivity,
  statusInfo,
} from "./detailLogic";
import { extendAction } from "./extendAction";

// Tone-gradient status card — live state (time remaining), the reviewer who
// recertifies it, and the grant window folded in.
const AccessStatusCard = ({
  access,
  lines,
}: {
  access: MFSelectableResourceAccess;
  lines?: MFRequestLine[] | null;
}) => {
  const m = accessMetrics(access);
  const info = statusInfo(access);
  const isExpired = access.status === "expired";
  const tint = info.tone; // success | warning | error
  const gradient =
    tint === "error"
      ? "linear-gradient(to right, var(--chakra-colors-error-50), var(--chakra-colors-neutral-25))"
      : tint === "warning"
        ? "linear-gradient(to right, var(--chakra-colors-warning-50), var(--chakra-colors-neutral-25))"
        : "linear-gradient(to right, var(--chakra-colors-success-50), var(--chakra-colors-neutral-25))";
  const StatusIcon = isExpired
    ? TbClockX
    : tint === "success"
      ? TbCircleCheck
      : TbClock;

  const reviewer = access.reviewedBy;

  return (
    <Box
      bg={gradient}
      borderRadius="12px"
      boxShadow="elevLow"
      p="lg"
      display="flex"
      flexDirection="column"
      gap="md"
    >
      <HStack spacing="1xs">
        <Icon as={StatusIcon} boxSize="18px" color={`${tint}.600`} />
        <Text fontSize="15px" fontWeight={600} color={`${tint}.800`}>
          {info.label}
        </Text>
      </HStack>

      {m.indefinite ? (
        <Text fontSize="smd" color="neutral.700">
          This access doesn’t expire.
        </Text>
      ) : isExpired ? (
        <Text fontSize="smd" fontWeight={500} color="error.700">
          {expiredAgoLabel(access.expiresAt)}
        </Text>
      ) : (
        <Box>
          <Bar value={remainingBar(m).pct} tone={info.barTone} />
          <Text fontSize="xs" color="neutral.700" mt="1xs">
            {remainingLabel(m.daysLeft)}
          </Text>
        </Box>
      )}

      {!isExpired && reviewer && (
        <Flex align="center" gap="sm">
          <Avatar
            size="sm"
            name={reviewer.name}
            src={reviewer.imageUrl ?? undefined}
            ring
          />
          <Text fontSize="smd" color="neutral.800">
            Reviewed by{" "}
            <Box as="span" fontWeight={600} color="neutral.950">
              {reviewer.name}
            </Box>
          </Text>
        </Flex>
      )}

      {lines?.length ? (
        <Flex
          direction="column"
          gap="1xs"
          pt="md"
          borderTopWidth="1px"
          borderColor="blackAlpha.200"
        >
          <Text fontSize="smd" color="neutral.700">
            Roles
          </Text>
          <RequestLines lines={lines} />
        </Flex>
      ) : null}

      <Flex
        direction="column"
        gap="1xs"
        pt="md"
        borderTopWidth="1px"
        borderColor="blackAlpha.200"
      >
        <MetaRow label="Granted">{grantWindow(access).granted}</MetaRow>
        <MetaRow label="Expires">{grantWindow(access).expires}</MetaRow>
      </Flex>
    </Box>
  );
};

const AccessDetailDrawer = ({
  access,
  onClose,
}: {
  access: MFSelectableResourceAccess | null;
  onClose: () => void;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  // Keep the last-shown grant mounted through the close animation.
  const [shown, setShown] = useState<MFSelectableResourceAccess | null>(access);
  const [renewing, setRenewing] = useState(false);
  useEffect(() => {
    if (access) setShown(access);
  }, [access]);

  const { mutate: decide, isLoading: extending } = MF_ACCESS.useDecideExtension(
    shown?.id ?? "",
  );

  // When this grant came through the request flow, fetch its originating request
  // so the Activity timeline shows the full lifecycle (requested → approved →
  // provisioning → granted → …), the same event log the request drawer renders.
  // suspense:false so the lazy fetch never blanks the page behind the route
  // fallback; disabled (no fetch) when there's no originating request, in which
  // case resolveGrantActivity falls back to the minimal grant-only timeline.
  const detail = MF_ACCESS.useGetAccessRequest(
    shown?.originatingRequestId ?? null,
    { suspense: false, live: Boolean(access) },
  );

  const {
    revoking,
    submitting,
    confirming: confirmingRevoke,
    askToRevoke,
    confirmModal: revokeConfirm,
  } = useRevokeAccessFlow(
    shown?.id ?? "",
    shown ? accessRowIdentity(shown).itemName : "",
    onClose,
  );
  // Held open while its own removal runs: the grant it shows is that removal's
  // key, and the removal closes it when done.
  const close = submitting ? () => undefined : onClose;

  const activity = useMemo(
    () => (shown ? resolveGrantActivity(shown, detail) : []),
    [shown, detail],
  );

  if (!shown) return null;

  const { itemName, crumbs, logoUrl } = accessRowIdentity(shown);
  const status = statusInfo(shown);
  const isExpired = shown.status === "expired";
  const canExtend = Boolean(shown.canExtend);

  const notYet =
    shown.checkExtensionAt &&
    !isAfter(new Date(), new Date(shown.checkExtensionAt));
  const extendTip = !canExtend
    ? notYet
      ? `Can be extended after ${humanDt(new Date(shown.checkExtensionAt!))}`
      : "Not currently eligible for extension"
    : null;

  const doExtend = () => {
    const action = extendAction(shown);
    if (action.kind === "navigate") {
      navigate(action.path);
      return;
    }
    decide({ result: "extend", selectableResourceAccessId: shown.id }).then(() =>
      toast({ status: "success", variant: "subtle", title: "Access extended" }),
    );
  };

  const footer = (
    <>
      {isExpired ? (
        <PloyButton
          variant="primary"
          leftIcon={<Icon as={TbRefresh} boxSize="16px" />}
          onClick={() => setRenewing(true)}
        >
          Renew access
        </PloyButton>
      ) : (
        <PloyButton
          variant="primary"
          leftIcon={<Icon as={TbRefresh} boxSize="16px" />}
          isDisabled={!canExtend || extending || submitting}
          title={extendTip ?? undefined}
          onClick={doExtend}
        >
          Extend access
        </PloyButton>
      )}
      {!isExpired && shown.canRevoke && (
        <PloyButton
          variant="secondary"
          isDisabled={revoking}
          isLoading={submitting}
          onClick={askToRevoke}
        >
          Revoke access
        </PloyButton>
      )}
      <PloyButton
        variant="secondary"
        ml="auto"
        isDisabled={submitting}
        onClick={onClose}
      >
        Close
      </PloyButton>
    </>
  );

  return (
    <>
      <DetailCard
        // Step aside while the revoke confirmation is up, as the request drawer
        // does for cancel: stacked overlays wash the dialog out.
        isOpen={Boolean(access) && !confirmingRevoke}
        returnFocusOnClose={!confirmingRevoke}
        onClose={close}
        header={{
          itemName,
          crumbs,
          logoUrl,
          statusChip: { tone: status.tone, label: status.label },
          // The request this grant came from, when it was signed with a passkey.
          badge: <SignedBadge signatures={detail?.signatures} />,
        }}
        statusLabel="Status"
        statusSlot={<AccessStatusCard access={shown} lines={detail?.lines} />}
        history={{
          priorRequests: shown.priorRequests,
          teamAdoption: shown.teamAdoption,
          catalogItemName: shown.appName,
          catalogName: shown.catalogName,
        }}
        activity={activity}
        footer={footer}
      />

      {revokeConfirm}

      {/* Renew re-opens the request flow focused on this catalog item — the same
          modal the Expired tab's Renew button opens. */}
      {renewing && (
        <Suspense fallback={null}>
          <RequestAccessModal
            isOpen
            onClose={() => setRenewing(false)}
            initialCatalogItemId={shown.catalogItemId}
          />
        </Suspense>
      )}
    </>
  );
};

export default AccessDetailDrawer;
